/*
  Cart Recommendation — <cart-recommendations> inside the cart drawer.
  Settings: Theme settings > Cart Recommendation (settings.cart_recommendation_*).

  - Static / Metafield sources are rendered by Liquid directly inside the drawer: no request here.
  - Shopify Product Recommendations: the drawer outputs an empty element with data-fetch-url
    ({routes.product_recommendations_url}?product_id=<FIRST cart product>&section_id=cart-recommendations),
    which this script loads once per (first product, cart products) and caches. If the first product
    changes while a request is running, that request is aborted and its result is never rendered.
  - Dawn re-renders the whole drawer after add / quantity change / remove, so a new element with the
    new first product connects and refreshes itself — no separate cart state is kept here.
  - ADD: one available variant -> /cart/add with the drawer sections, then cart-drawer.renderContents()
    (same as Dawn's product-form.js). Several variants -> the theme's own quick add popup
    (quick-add-modal + product-form), never a guessed variant.
*/
(function () {
  if (customElements.get('cart-recommendations')) return;

  var cache = new Map();
  var MAX_CACHE = 20;
  var controller = null;
  var pendingKey = null;
  var pendingPromise = null;
  var lastBody = '';

  function hashString(str) {
    var h = 0;
    for (var i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
    return (h >>> 0).toString(36);
  }

  /* --- lazy loading of the theme's own quick add files (only when a multi-variant ADD is used) --- */
  var loading = {};
  function loadScript(url, isDefined) {
    if (isDefined()) return Promise.resolve();
    var file = url.split('?')[0].split('/').pop();
    if (loading[file]) return loading[file];
    loading[file] = new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src*="/' + file + '"]');
      if (existing) {
        var tries = 0;
        (function check() {
          if (isDefined()) return resolve();
          if (++tries > 200) return reject(new Error(file));
          setTimeout(check, 50);
        })();
        return;
      }
      var s = document.createElement('script');
      s.src = url;
      s.defer = true;
      s.onload = function () { resolve(); };
      s.onerror = reject;
      document.head.appendChild(s);
    });
    return loading[file];
  }
  function loadStyle(url) {
    var file = url.split('?')[0].split('/').pop();
    if (document.querySelector('link[href*="/' + file + '"]')) return;
    var l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = url;
    document.head.appendChild(l);
  }
  function ensureQuickAdd(assets) {
    var parts = (assets || '').split('|');
    if (parts.length < 3) return Promise.reject(new Error('assets'));
    loadStyle(parts[0]);
    return loadScript(parts[1], function () { return !!customElements.get('product-form'); }).then(function () {
      return loadScript(parts[2], function () { return !!customElements.get('quick-add-modal'); });
    });
  }
  /* One shared quick add popup for all recommendation cards. Dawn's modal-dialog needs a
     .shopify-section ancestor when it connects (then moves itself to <body>). */
  function ensureModal(templateEl) {
    var modal = document.getElementById('QuickAdd-cart-recs');
    if (modal) return modal;
    if (!templateEl) return null;
    var wrap = document.createElement('div');
    wrap.className = 'shopify-section cart-recs-quick-add-section';
    wrap.id = 'shopify-section-cart-recs-quick-add';
    wrap.appendChild(templateEl.content.cloneNode(true));
    document.body.appendChild(wrap);
    return document.getElementById('QuickAdd-cart-recs');
  }
  // Closing the popup removes the body scroll lock Dawn set for the still-open drawer: restore it.
  document.body.addEventListener('modalClosed', function () {
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.classList.contains('active')) document.body.classList.add('overflow-hidden');
  });

  class CartRecommendations extends HTMLElement {
    constructor() {
      super();
      this.onClick = this.onClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onDragStart = function (e) {
        if (e.target.closest && e.target.closest('[data-cart-recs-track]')) e.preventDefault();
      };
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerdown', this.onPointerDown);
      this.addEventListener('dragstart', this.onDragStart);
      // Stop any change event from reaching Dawn's <cart-items>, which treats it as a quantity update.
      this.addEventListener('change', function (e) { e.stopPropagation(); });
      this.fetchUrl = this.dataset.fetchUrl;
      if (this.fetchUrl) this.load();
      else {
        var body = this.body();
        if (body && body.querySelector('.cart-recs__item')) lastBody = body.outerHTML;
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('dragstart', this.onDragStart);
    }

    body() {
      return this.querySelector('[data-cart-recs-body]');
    }

    get key() {
      return (this.dataset.fetchUrl || '') + '|' + (this.dataset.cartProductIds || '');
    }

    load() {
      var key = this.key;
      if (cache.has(key)) return this.paint(cache.get(key));
      // Avoid a layout jump: show the previous list (dimmed) until the new one is ready.
      if (lastBody) this.paint(lastBody, true);

      if (pendingKey !== key) {
        if (controller) controller.abort(); // first cart product changed: drop the stale request
        controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        pendingKey = key;
        var self = this;
        var ids = (this.dataset.cartProductIds || '').split(',').filter(Boolean);
        var exclude = this.dataset.excludeCart === 'true';
        var limit = parseInt(this.dataset.limit, 10) || 5;
        var url = this.fetchUrl + '&_cr=' + hashString(this.dataset.cartProductIds || '');
        pendingPromise = fetch(url, controller ? { signal: controller.signal } : {})
          .then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.text();
          })
          .then(function (text) {
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var body = doc.querySelector('[data-cart-recs-body]');
            var seen = {};
            var count = 0;
            if (body) {
              body.querySelectorAll('.cart-recs__item').forEach(function (li) {
                var id = li.getAttribute('data-product-id');
                if (seen[id] || (exclude && ids.indexOf(id) !== -1) || count >= limit) return li.remove();
                seen[id] = true;
                count++;
              });
            }
            var html = body && count ? body.outerHTML : '';
            if (cache.size >= MAX_CACHE) cache.delete(cache.keys().next().value);
            cache.set(key, html);
            return html;
          })
          .finally(function () {
            if (pendingKey === key) {
              pendingKey = null;
              pendingPromise = null;
            }
          });
      }
      var mine = this;
      pendingPromise
        .then(function (html) {
          // Paint only elements that still describe this cart state (never stale results).
          document.querySelectorAll('cart-recommendations[data-fetch-url]').forEach(function (el) {
            if (el.key === key) el.paint(html);
          });
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          if (mine.isConnected && mine.key === key) mine.paint('');
        });
    }

    paint(html, stale) {
      var current = this.body();
      if (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var next = tmp.firstElementChild;
        if (current) current.replaceWith(next);
        else this.prepend(next);
        if (!stale) lastBody = html;
      } else if (current) {
        current.innerHTML = '';
      }
      this.classList.toggle('is-stale', !!stale);
      this.hidden = !html;
    }

    /* ---------------- ADD ---------------- */
    onClick(event) {
      if (this.__dragged) {
        event.preventDefault();
        event.stopPropagation();
        this.__dragged = false;
        return;
      }
      var btn = event.target.closest('[data-cart-recs-add]');
      if (!btn || !this.contains(btn) || btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      var card = btn.closest('.cart-recs__item');
      this.setError(card, '');
      if (btn.dataset.mode === 'single') return this.addToCart(btn.dataset.variantId, btn, card);
      if (btn.dataset.mode === 'quick-add') return this.openQuickAdd(btn);
    }

    openQuickAdd(btn) {
      var self = this;
      var template = this.querySelector('template[data-cart-recs-quick-add-template]');
      var shared = window.customProductCard && window.customProductCard.ensureQuickAdd;
      var ready = shared ? window.customProductCard.ensureQuickAdd() : ensureQuickAdd(this.dataset.quickAddAssets);
      btn.classList.add('is-loading');
      ready
        .then(function () {
          var modal = ensureModal(template);
          if (!modal || typeof modal.show !== 'function') throw new Error('quick add');
          btn.classList.remove('is-loading');
          modal.show(btn); // Dawn fetches the product, shows its variant picker + product-form
        })
        .catch(function () {
          window.location.href = btn.dataset.productUrl;
        })
        .finally(function () {
          if (btn.isConnected) btn.classList.remove('is-loading');
        });
    }

    setError(card, message) {
      var el = card && card.querySelector('.cart-recs__error');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    addToCart(variantId, btn, card) {
      var self = this;
      var drawer = document.querySelector('cart-drawer');
      var sections = drawer && typeof drawer.getSectionsToRender === 'function'
        ? drawer.getSectionsToRender().map(function (s) { return s.id; })
        : ['cart-icon-bubble'];

      var config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      var formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);
      config.body = formData;

      btn.disabled = true;
      btn.classList.add('is-loading');
      btn.setAttribute('aria-busy', 'true');

      fetch(window.routes.cart_add_url, config)
        .then(function (r) { return r.json(); })
        .then(function (response) {
          if (response.status) {
            self.setError(card, response.description || response.message || (window.cartStrings && window.cartStrings.error));
            if (typeof publish === 'function') {
              publish(PUB_SUB_EVENTS.cartError, { source: 'cart-recommendations', productVariantId: variantId, errors: response.errors || response.description, message: response.message });
            }
            return;
          }
          // Source 'cart-items': the drawer is re-rendered right here, so Dawn's <cart-drawer-items>
          // must not fetch the same sections a second time.
          if (typeof publish === 'function') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', productVariantId: variantId, cartData: response });
          }
          if (drawer && typeof drawer.renderContents === 'function') {
            if (drawer.classList.contains('is-empty')) drawer.classList.remove('is-empty');
            drawer.renderContents(response); // new drawer HTML -> cart items, count, subtotal, recommendations
          } else if (response.sections && response.sections['cart-icon-bubble']) {
            var bubble = document.getElementById('cart-icon-bubble');
            var src = new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
            if (bubble && src) bubble.innerHTML = src.innerHTML;
          }
        })
        .catch(function () {
          self.setError(card, (window.cartStrings && window.cartStrings.error) || 'Error');
        })
        .finally(function () {
          if (btn.isConnected) {
            btn.disabled = false;
            btn.classList.remove('is-loading');
            btn.removeAttribute('aria-busy');
          }
        });
    }

    /* ---------------- mouse drag (touch & trackpads scroll natively) ---------------- */
    onPointerDown(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      var track = event.target.closest('[data-cart-recs-track]');
      if (!track || event.target.closest('button, select')) return;
      event.preventDefault();
      var self = this;
      var startX = event.clientX;
      var startLeft = track.scrollLeft;
      var moved = false;
      var lastDx = 0;
      function move(e) {
        var dx = e.clientX - startX;
        lastDx = dx;
        if (!moved && Math.abs(dx) > 5) {
          moved = true;
          track.classList.add('is-dragging');
        }
        if (moved) track.scrollLeft = startLeft - dx;
      }
      function up() {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        if (moved) {
          // settle like a swipe: > 30px moves one card in the drag direction
          var items = track.querySelectorAll('.cart-recs__item');
          var tl = track.getBoundingClientRect().left - track.scrollLeft;
          var lefts = Array.prototype.map.call(items, function (it) { return it.getBoundingClientRect().left - tl; });
          var current = 0;
          for (var i = 0; i < lefts.length; i++) if (Math.abs(lefts[i] - startLeft) < Math.abs(lefts[current] - startLeft)) current = i;
          var next = Math.max(0, Math.min(lefts.length - 1, current + (lastDx < -30 ? 1 : lastDx > 30 ? -1 : 0)));
          track.classList.remove('is-dragging');
          track.scrollTo({ left: lefts[next] - lefts[0], behavior: 'smooth' });
        }
        self.__dragged = moved;
        if (moved) setTimeout(function () { self.__dragged = false; }, 50);
      }
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    }
  }

  customElements.define('cart-recommendations', CartRecommendations);
})();
